import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Copy } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface WebhookEchoPayloadViewerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  log: {
    method: string;
    path: string | null;
    headers: Record<string, any>;
    body: Record<string, any>;
    query_params: Record<string, any>;
    created_at: string;
    ip_address: string | null;
    user_agent: string | null;
  } | null;
}

export function WebhookEchoPayloadViewer({
  open,
  onOpenChange,
  log,
}: WebhookEchoPayloadViewerProps) {
  const { toast } = useToast();

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copiado!',
      description: `${label} copiado para a área de transferência.`,
    });
  };

  if (!log) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh]">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Badge variant="outline">{log.method}</Badge>
              <span className="text-sm text-muted-foreground">{log.path || '/'}</span>
            </DialogTitle>
          </div>
          <div className="text-xs text-muted-foreground space-y-1">
            <div>IP: {log.ip_address || 'N/A'}</div>
            <div>Timestamp: {new Date(log.created_at).toLocaleString('pt-BR')}</div>
          </div>
        </DialogHeader>

        <Tabs defaultValue="body" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="body">Body</TabsTrigger>
            <TabsTrigger value="headers">Headers</TabsTrigger>
            <TabsTrigger value="query">Query Params</TabsTrigger>
          </TabsList>

          <TabsContent value="body" className="space-y-2">
            <div className="flex justify-end">
              <Button
                size="sm"
                variant="outline"
                onClick={() => copyToClipboard(JSON.stringify(log.body, null, 2), 'Body')}
              >
                <Copy className="h-4 w-4 mr-2" />
                Copiar
              </Button>
            </div>
            <ScrollArea className="h-[50vh] w-full rounded-md border">
              <pre className="p-4 text-sm">
                {JSON.stringify(log.body, null, 2)}
              </pre>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="headers" className="space-y-2">
            <div className="flex justify-end">
              <Button
                size="sm"
                variant="outline"
                onClick={() => copyToClipboard(JSON.stringify(log.headers, null, 2), 'Headers')}
              >
                <Copy className="h-4 w-4 mr-2" />
                Copiar
              </Button>
            </div>
            <ScrollArea className="h-[50vh] w-full rounded-md border">
              <pre className="p-4 text-sm">
                {JSON.stringify(log.headers, null, 2)}
              </pre>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="query" className="space-y-2">
            <div className="flex justify-end">
              <Button
                size="sm"
                variant="outline"
                onClick={() => copyToClipboard(JSON.stringify(log.query_params, null, 2), 'Query Params')}
              >
                <Copy className="h-4 w-4 mr-2" />
                Copiar
              </Button>
            </div>
            <ScrollArea className="h-[50vh] w-full rounded-md border">
              <pre className="p-4 text-sm">
                {Object.keys(log.query_params).length > 0
                  ? JSON.stringify(log.query_params, null, 2)
                  : '(Nenhum query parameter)'}
              </pre>
            </ScrollArea>
          </TabsContent>
        </Tabs>

        {log.user_agent && (
          <div className="mt-2 p-2 bg-muted rounded text-xs">
            <strong>User Agent:</strong> {log.user_agent}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
